import { useState, useEffect } from "react";
import api from "@/lib/api";
import { TeamBadge } from "@/components/TeamBadge";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

export function FinalTablePredictor({ leagueId, teams }) {
  const { t } = useI18n();
  const [order, setOrder] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/leagues/${leagueId}/table-prediction`);
      const teamMap = Object.fromEntries(teams.map((t) => [t.id, t]));
      let ids = data.ordering && data.ordering.length ? data.ordering : teams.map((t) => t.id);
      // ensure all teams present
      const missing = teams.map((t) => t.id).filter((id) => !ids.includes(id));
      ids = [...ids.filter((id) => teamMap[id]), ...missing];
      setOrder(ids.map((id) => teamMap[id]));
    })();
  }, [leagueId, teams]);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOrder(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/leagues/${leagueId}/table-prediction`, { ordering: order.map((t) => t.id) });
      toast.success(t("rank.saved"));
    } catch {
      toast.error(t("rank.saveErr"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline text-primary">{t("rank.overline")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("rank.hint")}</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-table-prediction-btn"
          className="bg-primary text-primary-foreground font-bold hover:brightness-110">
          <Save size={16} className="mr-2" /> {saving ? t("common.saving") : t("common.save")}
        </Button>
      </div>

      <div className="bg-card border border-border" data-testid="final-table-predictor">
        {order.map((team, i) => (
          <div
            key={team.id}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIndex != null) move(dragIndex, i); setDragIndex(null); }}
            className="flex items-center gap-3 px-4 py-3 border-t border-border first:border-t-0 hover:bg-secondary/50 transition-colors cursor-grab active:cursor-grabbing"
            data-testid={`predict-row-${team.short}`}
          >
            <GripVertical size={16} className="text-muted-foreground" />
            <span className="font-display text-xl w-6 text-center text-primary">{i + 1}</span>
            <TeamBadge team={team} size={26} />
            <span className="font-medium flex-1">{team.name}</span>
            <div className="flex gap-1">
              <button onClick={() => move(i, i - 1)} disabled={i === 0}
                className="p-1.5 border border-border hover:border-primary hover:text-primary disabled:opacity-30 transition-colors"
                data-testid={`move-up-${team.short}`}>
                <ChevronUp size={14} />
              </button>
              <button onClick={() => move(i, i + 1)} disabled={i === order.length - 1}
                className="p-1.5 border border-border hover:border-primary hover:text-primary disabled:opacity-30 transition-colors"
                data-testid={`move-down-${team.short}`}>
                <ChevronDown size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
